import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { ActivityIcon, NetworkIcon, MapIcon } from 'lucide-react';
import { LogoImage } from './LogoImage';
// Net icon als inline SVG voor fase 3
function NetIcon({
  size = 26,
  color = 'currentColor'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" data-id="element-366">
      {/* Horizontale lijnen */}
      <line x1="2" y1="7" x2="22" y2="7" data-id="element-367" />
      <line x1="2" y1="12" x2="22" y2="12" data-id="element-368" />
      <line x1="2" y1="17" x2="22" y2="17" data-id="element-369" />
      {/* Verticale/schuine lijnen */}
      <line x1="7" y1="4" x2="5" y2="20" data-id="element-370" />
      <line x1="12" y1="3" x2="12" y2="21" data-id="element-371" />
      <line x1="17" y1="4" x2="19" y2="20" data-id="element-372" />
      {/* Rand */}
      <path d="M2 7 Q12 3 22 7" data-id="element-373" />
      <path d="M2 17 Q12 21 22 17" data-id="element-374" />
    </svg>;
}
const steps = [{
  phase: '01',
  icon: 'activity',
  title: 'De Sonar: 90 dagen DataSprint',
  subtitle: 'Peilen waar de vis zit',
  desc: 'We lanceren een gerichte Google Ads-campagne als onze sonar. Niet als doel, maar als instrument. We meten welke zoekwoorden, titels en boodschappen daadwerkelijk leiden tot aanvragen in jouw specifieke markt. De data wordt geanalyseerd door een combinatie van slimme AI-systemen en menselijke expertise.',
  tag: 'Maand 1 tot 3',
  color: '#0066FF'
}, {
  phase: '02',
  icon: 'map',
  title: 'De Kaart: jouw CorePrint-rapport',
  subtitle: 'Je persoonlijke viskaart',
  desc: 'Je ontvangt het CorePrint-rapport: een complete marktanalyse met bewezen zoekwoorden, conversiepaden en een prioriteitenkaart voor je online aanwezigheid. Jij bent eigenaar van alle data.',
  tag: 'Week 12',
  color: '#0052CC'
}, {
  phase: '03',
  icon: 'net',
  title: 'Het Net: bouwen op bewijs',
  subtitle: 'Het net uitzetten op de juiste plek',
  desc: 'Op basis van de kaart bouwen we je SEO-structuur pagina voor pagina, keyword voor keyword, precies waar de vis zit. Inclusief Search Everywhere Optimization voor alle AI-gedreven zoekmachines. Terwijl organisch verkeer groeit, schalen we de advertentie-uitgaven af.',
  tag: 'Maand 4 en verder',
  color: '#28C840'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
export function Process() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="hoe" ref={ref} className="py-24 relative overflow-hidden noise-overlay gradient-mesh" style={{
    background: 'linear-gradient(160deg, #050A14 0%, #0D1628 100%)'
  }} data-id="element-375">
      <div className="absolute inset-0 grid-bg opacity-50" data-id="element-376" />
      <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] rounded-full opacity-15 pointer-events-none blur-3xl" style={{
      background: 'radial-gradient(ellipse, #0066FF, transparent)'
    }} animate={{
      scale: [1, 1.2, 1],
      opacity: [0.1, 0.2, 0.1]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-377" />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6" data-id="element-378">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        type: 'spring',
        stiffness: 100
      }} data-id="element-379">
          <div className="section-label-dark" data-id="element-380">Het systeem</div>
          <h2 className="text-[clamp(2.2rem,4vw,3.2rem)] font-black text-white leading-tight tracking-tight" data-id="element-381">
            Hoe CorePrint werkt
          </h2>
          <p className="text-white/50 mt-5 max-w-[540px] mx-auto text-[1.08rem]" data-id="element-382">
            Drie fases. Één doel: de sonar uitzetten en voorspelbaar groeien
            buiten je netwerk.
          </p>
        </motion.div>

        <div className="relative" data-id="element-383">
          {/* Connector line — schoon en subtiel, loopt langs de bovenkant van de iconen */}
          <motion.div className="absolute hidden lg:block" style={{
          top: 72,
          left: '6%',
          right: '6%',
          height: 1,
          background: 'linear-gradient(90deg, transparent 0%, rgba(0,102,255,0.5) 15%, rgba(0,102,255,0.7) 50%, rgba(0,102,255,0.5) 85%, transparent 100%)'
        }} initial={{
          scaleX: 0,
          originX: 0
        }} animate={inView ? {
          scaleX: 1
        } : {}} transition={{
          duration: 1.5,
          delay: 0.5,
          ease: 'easeOut'
        }} data-id="element-384" />

          <motion.div className="grid lg:grid-cols-3 gap-6" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-385">
            {steps.map((step, i) => <motion.div key={step.phase} className="card-dark p-9 cursor-default" variants={itemVariants} whileHover={{
            y: -8,
            transition: {
              type: 'spring',
              stiffness: 300,
              damping: 20
            }
          }} data-id="element-386">
                {/* Phase number + icon */}
                <div className="flex items-center gap-5 mb-7" data-id="element-387">
                  <motion.div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{
                background: `${step.color}18`,
                border: `1px solid ${step.color}40`
              }} whileHover={{
                scale: 1.1,
                rotate: 5,
                transition: {
                  type: 'spring',
                  stiffness: 300
                }
              }} data-id="element-388">
                    {step.icon === 'activity' && <ActivityIcon size={26} style={{
                  color: step.color
                }} data-id="element-389" />}
                    {step.icon === 'map' && <MapIcon size={26} style={{
                  color: step.color
                }} data-id="element-390" />}
                    {step.icon === 'net' && <NetIcon size={26} color={step.color} data-id="element-391" />}
                  </motion.div>
                  <div data-id="element-392">
                    <div className="text-[0.7rem] font-black tracking-widest uppercase" style={{
                  color: step.color
                }} data-id="element-393">
                      Fase {step.phase}
                    </div>
                    <div className="text-white/40 text-xs mt-1 font-medium" data-id="element-394">
                      {step.tag}
                    </div>
                  </div>
                </div>

                <h3 className="font-black text-white text-xl mb-2" data-id="element-395">
                  {step.title}
                </h3>
                <div className="text-[0.85rem] font-bold mb-5" style={{
              color: step.color
            }} data-id="element-396">
                  {step.subtitle}
                </div>
                <p className="text-white/55 text-[0.95rem] leading-relaxed" data-id="element-397">
                  {step.desc}
                </p>

                <motion.div className="mt-9 h-1 rounded-full" style={{
              background: `linear-gradient(90deg, ${step.color}, transparent)`
            }} initial={{
              scaleX: 0,
              originX: 0
            }} animate={inView ? {
              scaleX: 1
            } : {}} transition={{
              duration: 0.8,
              delay: 0.6 + i * 0.2,
              type: 'spring',
              stiffness: 100
            }} data-id="element-398" />
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}